import AlertDialog from "@/src/components/atom/AlertDialog";
import Badge from "@/src/components/atom/Badge";
import { Button } from "@/src/components/atom/Button";
import Loader from "@/src/components/atom/Loader";
import Radio from "@/src/components/atom/Radio";
import { StyledRadioGroup } from "@/src/components/atom/RadioGroup";
import Typo from "@/src/components/atom/Typo";
import SectionCard from "@/src/components/molecule/SectionCard";
import SectionCardRow from "@/src/components/molecule/SectionCardRow";
import useAlert from "@/src/hooks/useAlert";
import { useAppSelector } from "@/src/store";
import {
  useAddDispatcherOptionMutation,
  useLazyGetDispatcherOptionQuery,
  useUpdateDispatcherOptionMutation,
} from "@/src/store/apis/dispatcherOption";
import { AlarmType } from "@/src/store/apis/dispatcherOption/interface";
import {
  useDeletePaymentMethodsMutation,
  useGetPaymentMethodsQuery,
  useUpdatePaymentMethodsMutation,
} from "@/src/store/apis/subscription";
import { PaymentMethodDto } from "@/src/store/apis/subscription/interface";
import colorSet from "@/src/styles/color";
import {
  formatCreditCardExpiryDate,
  formatCreditCardMasking,
} from "@/src/utils/format";
import { isUndefined } from "@/src/utils/is";
import { CSSProperties, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import PaymentMethodChangeDialog from "../../Credit/components/dialog/PaymentMethodChangeDialog";
import AddPaymentMethodsDialog from "./dialog/AddPaymentMethodsDialog";
import { getCardTypeAsync } from "@/src/utils/cardUtils";

enum DialogState {
  NULL,
  PAYMENT_METHODS_ADD,
}

enum AlertDialogState {
  NULL,
  MAIN_PAYMENT_METHODS,
  PAYMENT_METHODS_CHANGE,
  PAYMENT_METHODS_DELETE,
}

type AlarmStateType = "PUSH_ALARM_AND_EMAIL" | "PUSH_ALARM" | "EMAIL";
const emptyArray: PaymentMethodDto[] = [];

interface PaymentMethodDetailProps {
  payment: PaymentMethodDto;
  isBusiness: boolean;
  subscriptionStatus?: string;
  setAlertDialogState: (state: AlertDialogState) => void;
  handlePaymentMethodDeleteClick: () => void;
  alarms: AlarmStateType | undefined;
  handleChangeAlarms: (value: string) => void;
}

const PaymentMethodDetail: React.FC<PaymentMethodDetailProps> = ({
  payment,
  isBusiness,
  subscriptionStatus,
  setAlertDialogState,
  handlePaymentMethodDeleteClick,
  alarms,
  handleChangeAlarms,
}) => {
  const { t } = useTranslation();
  const [cardTypeInfo, setCardTypeInfo] = useState<{
    type: string;
    icon: string | null;
    displayName: string;
  } | undefined>(undefined);

  const { cardNumber, expiryDate, userInfo } = payment;

  useEffect(() => {
    getCardTypeAsync(cardNumber).then((type) => {
      setCardTypeInfo(type);
    });
  }, [cardNumber]);

  return (
    <Flex $direction="row" $gap={16}>
      <Flex $direction="column" $gap={16}>
        <Flex $gap={8} style={{ display: "flex", alignItems: "center" }}>
          {cardTypeInfo?.icon && (
            <img
              src={cardTypeInfo.icon}
              alt={`${cardTypeInfo.type} card`}
              style={{ width: "28px", height: "28px" }}
            />
          )}
          <Typo typoType="h4">{cardTypeInfo?.displayName}</Typo>
        </Flex>
        <Flex $direction="column" $gap={8}>
          <SectionCardRow
            label={
              <Typo color="gray6" typoType="b7m">
                {t("subscriptionManagement:subscription.cardInformation")}
              </Typo>
            }
            value={
              <Flex $gap={8} $alignItems="center">
                <Typo typoType="b7r">
                  {formatCreditCardMasking(cardNumber)}
                </Typo>
                <Badge
                  badgeSize="S"
                  badgeColor="gray10"
                  color="gray4"
                  text={
                    isBusiness
                      ? t("subscriptionManagement:subscription.business")
                      : t("subscriptionManagement:subscription.individual")
                  }
                />
              </Flex>
            }
          />
          <SectionCardRow
            label={
              <Typo color="gray6" typoType="b7m">
                {t("subscriptionManagement:subscription.cardExpirationDate")}
              </Typo>
            }
            value={
              <Typo typoType="b7r">
                {formatCreditCardExpiryDate(expiryDate)}
              </Typo>
            }
          />
        </Flex>

        <Flex $gap={8}>
          <Button
            buttonColor="black"
            buttonGrade="tertiary"
            onClick={() => {
              setAlertDialogState(AlertDialogState.PAYMENT_METHODS_CHANGE);
            }}
            buttonSize={32}
            className="button-full-size"
          >
            {t("subscriptionManagement:subscription.button.change")}
          </Button>
          {subscriptionStatus === "CANCELED" && (
            <Button
              buttonColor="black"
              buttonGrade="tertiary"
              onClick={handlePaymentMethodDeleteClick}
              buttonSize={32}
              className="button-full-size"
            >
              {t("common:delete")}
            </Button>
          )}
        </Flex>
      </Flex>
      <Divider />
      <Flex $direction="column">
        <StyledSectionCardRow
          label={t(
            "subscriptionManagement:subscription.paymentCompletionNotification"
          )}
          value={
            <Flex $justifyContent="space-between">
              <StyledRadioGroup
                size="large"
                name="alarms"
                value={alarms}
                onChange={(e) => {
                  handleChangeAlarms(e.target.value);
                }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "12px",
                  flex: "1 0 0",
                }}
              >
                <Radio
                  value="EMAIL"
                  style={{
                    display: "flex",
                    width: "100%",
                    height: "48px",
                    padding: "12px 16px",
                    alignItems: "center",
                    gap: "8px",
                    borderRadius: "8px",

                    border: `1px solid ${alarms === "EMAIL" ? colorSet.gray7 : colorSet.gray9}`,
                  }}
                  color={alarms === "EMAIL" ? "indigo" : "gray6"}
                >
                  {t("subscriptionManagement:subscription.email")}
                </Radio>
                <Radio
                  value="PUSH_ALARM"
                  style={{
                    display: "flex",
                    width: "100%",
                    padding: "12px 16px",
                    height: "48px",
                    alignItems: "center",
                    gap: "8px",
                    borderRadius: "8px",
                    border: `1px solid ${alarms === "PUSH_ALARM" ? colorSet.gray7 : colorSet.gray9}`,
                  }}
                  color={alarms === "PUSH_ALARM" ? "indigo" : "gray6"}
                >
                  {t("subscriptionManagement:subscription.push")}
                </Radio>
                <Radio
                  value="PUSH_ALARM_AND_EMAIL"
                  style={{
                    display: "flex",
                    width: "100%",
                    padding: "12px 16px",
                    height: "48px",
                    alignItems: "center",
                    gap: "8px",
                    borderRadius: "8px",
                    border: `1px solid ${alarms === "PUSH_ALARM_AND_EMAIL" ? colorSet.gray7 : colorSet.gray9}`,
                  }}
                  color={
                    alarms === "PUSH_ALARM_AND_EMAIL" ? "indigo" : "gray6"
                  }
                >
                  {t("subscriptionManagement:subscription.emailAndPush")}
                </Radio>
              </StyledRadioGroup>
            </Flex>
          }
        />
      </Flex>
    </Flex>
  );
};

function PaymentManagement({
  subscriptionStatus,
  onPaymentMethodsChange,
}: {
  subscriptionStatus?: string;
  onPaymentMethodsChange?: (hasPayments: boolean) => void;
}) {
  const { t } = useTranslation();
  const alert = useAlert();
  const user = useAppSelector((state) => state.auth.user);
  const [alarms, setAlarms] = useState<AlarmStateType | undefined>(undefined);
  const [dialogState, setDialogState] = useState<DialogState>(DialogState.NULL);
  const [alertDialogState, setAlertDialogState] = useState<AlertDialogState>(
    AlertDialogState.NULL
  );
  const [selectPayment, setSelectPayment] = useState<PaymentMethodDto | null>(
    null
  );

  // API
  const [deletePayment, { isLoading: isDeletePaymentLoading }] =
    useDeletePaymentMethodsMutation();
  const [updatePayment] = useUpdatePaymentMethodsMutation();
  const { payments, isFetching } = useGetPaymentMethodsQuery(
    { page: 1, pageSize: 2 },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData, isFetching, isError }) => {
        const isUnstable = isFetching || isError || isUndefined(currentData);
        const isStable = !isUnstable;

        return {
          isFetching,
          payments: isStable ? currentData.rows : emptyArray,
        };
      },
    }
  );
  const [getDispatcherOptionFetch] = useLazyGetDispatcherOptionQuery();
  const [updateDispatcherOption] = useUpdateDispatcherOptionMutation();
  const [addDispatcherOption] = useAddDispatcherOptionMutation();

  const handlePaymentMethodDeleteClick = () => {
    if (payments.length > 0) {
      setSelectPayment(payments[0]);
    }
    setAlertDialogState(AlertDialogState.PAYMENT_METHODS_DELETE);
  };

  const handleDeletePayment = async (id: number) => {
    try {
      await deletePayment({ id }).unwrap();

      setAlertDialogState(AlertDialogState.NULL);
      alert.showAlert({
        type: "success",
        message: t("alert:saveSuccess"),
      });
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const getDispatcherOption = useCallback(async () => {
    try {
      const res = await getDispatcherOptionFetch("PAYMENT_SUCCESS").unwrap();

      if (res?.alarmTypeList.length >= 2) {
        // 푸시알림, 이메일 모두 사용중
        return setAlarms("PUSH_ALARM_AND_EMAIL");
      } else {
        return setAlarms(res?.alarmTypeList[0] as "EMAIL" | "PUSH_ALARM");
      }
    } catch (e: any) {
      if (e.status === 404) {
        // 값이 없을경우 알림을 생성해주어야함 default로 이메일 & 푸시 알림 설정
        await addDispatcherOption({
          dispatcherKey: "SUBSCRIPTION_PAYMENT_NOTICE",
          alarmTypeList: ["PUSH_ALARM", "EMAIL"],
        });
        await addDispatcherOption({
          dispatcherKey: "PAYMENT_SUCCESS",
          alarmTypeList: ["PUSH_ALARM", "EMAIL"],
        });

        return setAlarms("PUSH_ALARM_AND_EMAIL");
      }

      if (e.status !== 404) {
        const message = Array.isArray(e.data.message)
          ? e.data.message[0]
          : e.data.message;
        alert.showAlert({ type: "error", message });
      }
    }
  }, [addDispatcherOption, alert, getDispatcherOptionFetch]);

  const handleChangeAlarms = async (value: string) => {
    const params = {
      alarmTypeList: (value === "PUSH_ALARM_AND_EMAIL"
        ? ["PUSH_ALARM", "EMAIL"]
        : [value]) as AlarmType[],
    };

    try {
      await updateDispatcherOption({
        dispatcherKey: "SUBSCRIPTION_PAYMENT_NOTICE",
        alarmTypeList: params.alarmTypeList,
      });
      await updateDispatcherOption({
        dispatcherKey: "PAYMENT_SUCCESS",
        alarmTypeList: params.alarmTypeList,
      });

      await getDispatcherOption();

      alert.showAlert({
        type: "success",
        message: t("alert:saveSuccess"),
      });
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const updateMainPaymentMethods = async () => {
    if (!user || !selectPayment) {
      return;
    }

    const updateParams = {
      id: selectPayment.id,
      cardNumber: selectPayment.cardNumber,
      userInfo: selectPayment.userInfo,
      expiryDate: selectPayment.expiryDate,
      password2: selectPayment.password2,
      isRepresentative: true,
      exporterId: user.exporter.id,
    };

    try {
      await updatePayment(updateParams).unwrap();

      setAlertDialogState(AlertDialogState.NULL);
      alert.showAlert({
        type: "success",
        message: t("alert:saveSuccess"),
      });
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const renderPaymentMethods = () => {
    if (isFetching) {
      return (
        <LoaderContainer>
          <Loader size={50} />
        </LoaderContainer>
      );
    }

    if (payments.length === 0) {
      // 데이터 없을 시
      return null;
    }

    const paymentInfo = payments[0];
    const isBusiness = user?.exporter.businessNumber === paymentInfo.userInfo;

    return (
      <PaymentMethodDetail
        payment={paymentInfo}
        isBusiness={isBusiness}
        subscriptionStatus={subscriptionStatus}
        setAlertDialogState={setAlertDialogState}
        handlePaymentMethodDeleteClick={handlePaymentMethodDeleteClick}
        alarms={alarms}
        handleChangeAlarms={handleChangeAlarms}
      />
    );
  };

  const renderDialog = () => {
    if (dialogState === DialogState.PAYMENT_METHODS_ADD) {
      return (
        <AddPaymentMethodsDialog
          open
          onOpenChange={() => {
            setDialogState(DialogState.NULL);
          }}
        />
      );
    }
  };

  const renderAlertDialog = () => {
    if (alertDialogState === AlertDialogState.MAIN_PAYMENT_METHODS) {
      return (
        <AlertDialog
          open
          title={t("subscriptionManagement:subscription.changePaymentMethod")}
          onOpenChange={() => {
            setAlertDialogState(AlertDialogState.NULL);
          }}
          onOk={updateMainPaymentMethods}
          okText={t("subscriptionManagement:subscription.button.ok")}
          cancelText={t("subscriptionManagement:subscription.button.cancel")}
        >
          {t(
            "subscriptionManagement:subscription.changePaymentMethodDescription"
          )}
        </AlertDialog>
      );
    }

    if (alertDialogState === AlertDialogState.PAYMENT_METHODS_DELETE) {
      return (
        <StyledAlertDialog
          open
          title={t("subscriptionManagement:subscription.paymentDelete")}
          onOpenChange={() => {
            setAlertDialogState(AlertDialogState.NULL);
          }}
        >
          <Flex $direction="column" $gap={24}>
            <Typo>
              {t(
                "subscriptionManagement:subscription.paymentDeleteDescription"
              )}
            </Typo>
            <Flex $gap={8}>
              <StyledButton
                buttonGrade="tertiary"
                buttonColor="black"
                onClick={() => {
                  setAlertDialogState(AlertDialogState.NULL);
                }}
              >
                {t("subscriptionManagement:subscription.button.cancel")}
              </StyledButton>
              <StyledButton
                isLoading={isDeletePaymentLoading}
                disabled={isDeletePaymentLoading}
                onClick={() => {
                  selectPayment?.id && handleDeletePayment(selectPayment?.id);
                }}
              >
                {t("subscriptionManagement:subscription.button.ok")}
              </StyledButton>
            </Flex>
          </Flex>
        </StyledAlertDialog>
      );
    }

    if (alertDialogState === AlertDialogState.PAYMENT_METHODS_CHANGE) {
      return (
        <PaymentMethodChangeDialog
          open
          onOpenChange={() => {
            setAlertDialogState(AlertDialogState.NULL);
          }}
        />
      );
    }
  };

  useEffect(() => {
    getDispatcherOption();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 결제수단 정보 변경 시 부모 컴포넌트에 알림
  useEffect(() => {
    if (onPaymentMethodsChange) {
      onPaymentMethodsChange(payments.length > 0);
    }
  }, [payments.length, onPaymentMethodsChange]);

  return payments.length > 0 ? (
    <SectionCard
      cardTitle={t(
        "subscriptionManagement:subscription.managePaymentMethodsCardTitle"
      )}
    >
      {renderDialog()}
      {renderAlertDialog()}
      {renderPaymentMethods()}
    </SectionCard>
  ) : null;
}

export default PaymentManagement;

const Divider = styled.div`
  align-self: stretch;
  border-right: 1px solid ${colorSet.gray9};
`;

const Flex = styled.div<{
  $direction?: CSSProperties["flexDirection"];
  $alignItems?: CSSProperties["alignItems"];
  $justifyContent?: CSSProperties["justifyContent"];
  $gap?: number;
}>`
  display: flex;
  flex-direction: ${({ $direction }) => $direction};
  align-items: ${({ $alignItems }) => $alignItems};
  justify-content: ${({ $justifyContent }) => $justifyContent};
  gap: ${({ $gap }) => $gap || 0}px;
  width: 100%;
  height: 100%;

  .button-full-size {
    flex: 1;
    text-align: center;
  }

  .text-center {
    text-align: center;
  }
`;

const StyledSectionCardRow = styled(SectionCardRow)`
  .section-label {
    max-width: 100%;
  }
  .section-row-single-value {
    max-width: 100%;
  }

  & > :last-child {
    flex: 1;
  }
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 182px;
`;

const StyledButton = styled(Button)`
  flex: 1;
  text-align: center;
`;

const StyledAlertDialog = styled(AlertDialog)`
  & {
    gap: 0;
  }
`;
